import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-semibold ring-offset-background transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        // Default = Azul CTA - Para botones de acción principales (Agregar carrito, Comprar, Cotizar)
        default: "bg-cta text-cta-foreground hover:bg-cta-hover shadow-lg hover:shadow-xl hover:scale-[1.02] rounded-full",
        // Primary = Verde Bosque - Para acciones secundarias importantes
        primary: "bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg hover:shadow-xl hover:scale-[1.02] rounded-full",
        // Secondary = Verde Oliva - Para acciones terciarias
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/90 shadow-md hover:shadow-lg hover:scale-[1.02] rounded-full",
        // Destructive
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90 rounded-full",
        // Outline con verde
        outline: "border-2 border-primary bg-transparent text-primary hover:bg-primary hover:text-primary-foreground rounded-full",
        // Outline CTA (azul)
        "outline-cta": "border-2 border-cta bg-transparent text-cta hover:bg-cta hover:text-cta-foreground rounded-full",
        // Outline secondary (para hero sobre fondos claros)
        "outline-secondary": "border-2 border-foreground/20 bg-transparent text-foreground hover:bg-foreground/5 rounded-full",
        // Ghost
        ghost: "hover:bg-accent/60 hover:text-accent-foreground rounded-lg",
        // Link
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-12 px-7 py-2.5",
        sm: "h-11 px-5",
        lg: "h-14 px-10 text-base",
        icon: "h-11 w-11 rounded-full",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
